import AppCommandBar from 'app/AppCommandBar'
import { FlexSpinner } from 'components'
import { StickyPane } from 'components/StickyPane'
import { ConfirmDeleteDialog } from 'dialogs/ConfirmDeleteDialog'
import { Stack } from 'forms/FormStack'
import { canUpdateProving } from 'helpers'
import { useAuthContext } from 'hooks/useAuthContext'
import { useCommand } from 'hooks/useCommand'
import { useDialogState } from 'hooks/useDialogState'
import useSelection from 'hooks/useSelection'
import { Mail, MailFolder, Proving, UserRole } from 'model'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUpdateProvingMutation } from 'views/ProvingDetailsView/hooks'
import { MailboxList, MailboxTabs, View } from './index'
import {
  useDeleteMailsMutation,
  useLoadMailsQuery,
  useUpdateMailMutation,
} from './Mailbox.generated'
import { MailboxForm, MailboxFormMode } from './MailboxForm'

export interface MailboxProps {
  proving?: Proving
}

const MailboxListView: React.FC<MailboxProps> = ({ proving }) => {
  const { t } = useTranslation()
  // const [deleteMail] = useDeleteMailMutation()
  const [deleteMails] = useDeleteMailsMutation()
  const [updateMail] = useUpdateMailMutation()
  const [updateProving] = useUpdateProvingMutation()
  const [tab, setTab] = useState(MailFolder.Inbox)
  const [selectedMailId, setSelectedMailId] = useState()

  const [selection, selectedItems] = useSelection<
    Pick<
      Mail,
      | '_id'
      | 'subject'
      | 'created'
      | 'receivedAt'
      | 'sentAt'
      | 'from'
      | 'body'
      | 'attachments'
      | 'to'
      | 'cc'
      | 'proving'
      | 'isRead'
    >
  >({
    getKey: (item) => item._id!,
  })
  const [unreadMailCount, setUnreadMailCount] = useState(
    (proving && proving.newMails) || 0
  )
  const [showForm, setShowForm] = useState()
  const [formMode, setFormMode] = useState(MailboxFormMode.New)
  const [dialogState, showDialog, hideDialog] = useDialogState()
  let canUpdateCurrentProving = proving ? canUpdateProving(proving) : true
  const auth = useAuthContext()
  const authUserId = auth && auth.user && auth.user._id
  const serviceTeam = auth.hasUserRole(UserRole.ServiceTeam)
  const serviceTeamPermissions =
    proving &&
    proving.provingGround &&
    proving.provingGround.authorizedEditors &&
    proving.provingGround.authorizedEditors.find(
      (ae: any) => ae._id === authUserId
    )
  const isServiceTeam = serviceTeam && serviceTeamPermissions ? true : false
  if (isServiceTeam === true) {
    canUpdateCurrentProving = isServiceTeam
  }
  const { data, loading, error, refetch } = useLoadMailsQuery({
    variables: proving
      ? {
          provingId: proving._id,
          filter: { folder: { EQ: tab } },
        }
      : {
          provingId: null,
          filter: { folder: { EQ: tab } },
        },

    pollInterval: showForm ? 0 : 60000,
  })
  const { mails } = data && data.mails ? data : { mails: [] }

  /*
  const sort =
    tab === MailFolder.Inbox
      ? ({ receivedAt: SortType.Desc } as MailSortType)
      : tab === MailFolder.Sent
      ? ({ sentAt: SortType.Desc } as MailSortType)
      : ({ created: SortType.Desc } as MailSortType)
      */

  const handleConfirmDelete = React.useCallback(async () => {
    const ids = selectedItems.map((sel) => sel._id)
    await deleteMails({
      variables: {
        ids,
      },
    })

    // await deleteMail({
    //   variables: {
    //     filter: selectedItems[0]._id,
    //   },
    // })
    selection.setAllSelected(false)
    refetch()
    hideDialog()
  }, [selectedItems, hideDialog, selection, refetch, deleteMails])

  const handleUnreadUpdate = React.useCallback(async () => {
    if (proving) {
      setUnreadMailCount(unreadMailCount - 1)
      await updateProving({
        variables: {
          id: proving.id,
          update: {
            newMails: unreadMailCount - 1,
          },
        },
      })
    }

    await updateMail({
      variables: {
        id: selectedItems[0]._id,
        update: {
          isRead: true,
        },
      },
    })
    await refetch()
  }, [
    selectedItems,
    updateMail,
    updateProving,
    proving,
    unreadMailCount,
    refetch,
  ])

  const handleProvingSetToUnreadSubmit = React.useCallback(async () => {
    if (proving && selectedItems[0].isRead) {
      setUnreadMailCount(unreadMailCount + 1)
      await updateProving({
        variables: {
          id: proving.id,
          update: {
            newMails: unreadMailCount + 1,
          },
        },
      })

      await updateMail({
        variables: {
          id: selectedItems[0]._id,
          update: {
            isRead: false,
          },
        },
      })
    }
    await refetch()
  }, [
    proving,
    updateProving,
    updateMail,
    selectedItems,
    unreadMailCount,
    refetch,
  ])

  const handleSetToUnreadSubmit = React.useCallback(async () => {
    if (selectedItems[0].isRead) {
      await updateMail({
        variables: {
          id: selectedItems[0]._id,
          update: {
            isRead: false,
          },
        },
      })
    }
    await refetch()
  }, [updateMail, selectedItems, refetch])

  const handleSubmit = React.useCallback(
    async (emails) => {
      const batch: any[] = await emails
        .filter((email: any) => !email.isRead)
        .map((sel: any) => ({
          id: sel._id,
          update: {
            isRead: true,
          },
        }))
      if (batch.length > 0) {
        batch.forEach(async (b) => {
          await updateMail({
            variables: {
              ...b,
            },
          })
        })
      }

      if (proving) {
        setUnreadMailCount(0)
        await updateProving({
          variables: {
            id: proving.id,
            update: {
              newMails: 0,
            },
          },
        })
      }
      await refetch()
    },
    [proving, updateProving, updateMail, setUnreadMailCount, refetch]
  )

  useCommand({
    text: t('model.mail.create'),
    title: t('model.mail.create'),
    key: 'newMail',
    disabled: !canUpdateCurrentProving,
    priority: 10,
    onClick: () => {
      setFormMode(MailboxFormMode.New)
      setShowForm(true)
    },
    iconProps: { iconName: 'add' },
  })

  useCommand(
    {
      text: t('model.mail.view'),
      title: t('model.mail.view'),
      key: 'viewMail',
      priority: 10,
      onClick: () => {
        const mail = selectedItems[0]
        if (!selectedItems[0].isRead) {
          handleUnreadUpdate()
        }
        setSelectedMailId(mail._id)
        setFormMode(
          tab === 'draft' ? MailboxFormMode.EditDraft : MailboxFormMode.Edit
        )
        setShowForm(true)
      },
      disabled: selectedItems.length !== 1,
      iconProps: { iconName: 'view' },
    },
    [
      selectedItems,
      handleUnreadUpdate,
      setSelectedMailId,
      setFormMode,
      setShowForm,
    ]
  )

  useCommand(
    {
      text: t('model.mail.reply'),
      title: t('model.mail.reply'),
      key: 'replyMail',
      priority: 20,
      onClick: () => {
        const mail = selectedItems[0]
        setSelectedMailId(mail._id)
        setFormMode(MailboxFormMode.Reply)
        setShowForm(true)
      },
      disabled: selectedItems.length !== 1,
      iconProps: { iconName: 'Reply' },
    },
    [selectedItems, setSelectedMailId, setFormMode, setShowForm]
  )

  useCommand(
    {
      text: t('section.mails.markAsRead'),
      key: 'mailsRead',
      priority: 20,
      onClick: () => {
        handleSubmit(mails)
      },
      iconProps: { iconName: 'Read' },
    },
    [selectedItems, proving]
  )
  useCommand(
    {
      text: t('section.mails.markAsUnread'),
      key: 'mailsUnread',
      priority: 30,
      onClick: () => {
        proving ? handleProvingSetToUnreadSubmit() : handleSetToUnreadSubmit()
      },
      disabled: selectedItems.length !== 1,
      iconProps: { iconName: 'Mail' },
    },
    [
      selectedItems,
      proving,
      handleProvingSetToUnreadSubmit,
      handleSetToUnreadSubmit,
    ]
  )

  useCommand(
    {
      key: 'delete',
      farCommand: true,
      priority: 10,
      text: t('model.proving.mails.deleteMails'),
      hidden:
        selectedItems.length === 0 ||
        selectedItems.filter((x: any) => x.$change).length !== 0,
      iconProps: { iconName: 'Delete' },
      onClick: () => showDialog(),
    },
    [selectedItems, showDialog]
  )

  return (
    <FlexSpinner loading={loading} error={error}>
      <Stack verticalFill={true}>
        <Stack.Item>
          <MailboxTabs
            selectedPage={tab}
            onSelectPage={(selectedTab) => setTab(selectedTab as MailFolder)}
          />
        </Stack.Item>
        <Stack.Item grow={true}>
          {tab === 'inbox' && (
            <MailboxList
              view={View.inbox}
              items={mails}
              selection={selection}
              onItemInvoked={() =>
                selectedItems.length === 1
                  ? (() => {
                      if (!selectedItems[0].isRead) {
                        handleUnreadUpdate()
                      }
                      setSelectedMailId(selectedItems[0]._id)
                      setFormMode(MailboxFormMode.Edit)
                      setShowForm(true)
                    })()
                  : false
              }
            />
          )}
          {tab === 'outbox' && (
            <MailboxList
              view={View.outbox}
              items={mails}
              selection={selection}
              onItemInvoked={() =>
                selectedItems.length === 1
                  ? (() => {
                      if (!selectedItems[0].isRead) {
                        handleUnreadUpdate()
                      }
                      setSelectedMailId(selectedItems[0]._id)
                      setFormMode(MailboxFormMode.Edit)
                      setShowForm(true)
                    })()
                  : false
              }
            />
          )}
          {tab === 'sent' && (
            <MailboxList
              view={View.sent}
              items={mails}
              selection={selection}
              onItemInvoked={() =>
                selectedItems.length === 1
                  ? (() => {
                      if (!selectedItems[0].isRead) {
                        handleUnreadUpdate()
                      }
                      setSelectedMailId(selectedItems[0]._id)
                      setFormMode(MailboxFormMode.Edit)
                      setShowForm(true)
                    })()
                  : false
              }
            />
          )}
          {tab === 'draft' && (
            <MailboxList
              view={View.draft}
              items={mails}
              selection={selection}
              onItemInvoked={() =>
                selectedItems.length === 1
                  ? (() => {
                      if (!selectedItems[0].isRead) {
                        handleUnreadUpdate()
                      }
                      setSelectedMailId(selectedItems[0]._id)
                      setFormMode(MailboxFormMode.EditDraft)
                      setShowForm(true)
                    })()
                  : false
              }
            />
          )}
        </Stack.Item>
      </Stack>
      {showForm && (
        <MailboxForm
          isOpen={true}
          mailId={selectedMailId}
          proving={proving}
          mode={formMode}
          onSaved={async () => {
            await refetch()
            setShowForm(false)
          }}
          onDismiss={() => setShowForm(false)}
        />
      )}
      <ConfirmDeleteDialog
        {...dialogState}
        onConfirm={handleConfirmDelete}
        itemCount={selectedItems.length}
      />
    </FlexSpinner>
  )
}

export const Mailbox: React.FC<MailboxProps> = ({ proving }) => (
  <>
    {proving ? (
      <>
        <AppCommandBar />
        <MailboxListView proving={proving} />
      </>
    ) : (
      <StickyPane>
        <AppCommandBar />
        <MailboxListView proving={proving} />
      </StickyPane>
    )}
  </>
)
